import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETAPAS_FUNIL,
  MOTIVOS_ENCERRAMENTO,
  ROTULO_CANAL,
  ROTULO_ETAPA,
  ROTULO_MOTIVO_ENCERRAMENTO,
  ROTULO_SCORE,
  estaAberta,
  exigeMotivo,
  exigeProximoPasso,
  formatarReais,
  type CanalContato,
  type EtapaFunil,
  type MotivoEncerramento,
  type OrigemLead,
  type ResumoDoPainel,
  type Score,
  type TipoContrato,
} from '@reducee/contracts';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { Link } from 'react-router';

import { baixarArquivo, chamarApi, ErroDaApi } from '../api/cliente.ts';
import {
  ConversaoPorOrigem,
  ContatosDeHoje,
  ETAPAS_VISIVEIS,
  FaixaDoFunil,
  IndicadoresDeGestao,
  TabelaDeCadencias,
} from '../componentes/PainelComercial.tsx';
import { TabelaDeVendas } from '../componentes/TabelaDeVendas.tsx';
import { FilaDeAtendimento } from '../componentes/FilaDeAtendimento.tsx';
import { IniciarCadencia } from '../componentes/IniciarCadencia.tsx';
import { MoverEtapa } from '../componentes/MoverEtapa.tsx';
import { NovaOportunidade } from '../componentes/NovaOportunidade.tsx';
import { ResumoDoRoteiro, useModeloDeCadencia } from '../componentes/RoteiroDeCadencia.tsx';
import { Cartao, CabecalhoDePagina, data } from '../componentes/Superficie.tsx';
import { CampoSelecao, CampoTexto } from '../componentes/Campo.tsx';
import { useSessao } from '../autenticacao/sessao.tsx';
import { useOrigens } from '../dominio/origens.ts';

type Oportunidade = {
  id: string;
  produto: string;
  operadora: string | null;
  tipoContrato: TipoContrato;
  valorMensalCentavos: number;
  score: Score;
  etapa: EtapaFunil;
  proximoPasso: string | null;
  dataProximoPasso: string | null;
  motivoEncerramento: MotivoEncerramento | null;
  origem: OrigemLead | null;
  campanha: string | null;
  /** O que a pessoa respondeu na Meta, reduzido ao que cabe num card. Nulo
   * quando a Oportunidade não nasceu de Lead da Meta. */
  resumoDoFormulario: { mensalidadeAtual: string | null; idades: string | null } | null;
  criadaEm: string;
  primeiroBoletoPagoEm: string | null;
  tipoPlano: string | null;
  acomodacao: string;
  modalidade: string;
  planoAnterior: string | null;
  numeroVidas: number | null;
  prazoContratacao: string | null;
  urgencia: string;
  engajamento: string;
  canalComissao: string;
  parceiroId: string | null;
  contato: { id: string; nome: string; naoContatar: boolean };
  responsavel: { id: string; nome: string };
};


type UsuariaDaLista = { id: string; nome: string; ativa: boolean };

type Filtros = {
  busca: string;
  responsavelId: string;
  etapa: string;
  origem: string;
};

const FILTROS_VAZIOS: Filtros = { busca: '', responsavelId: '', etapa: '', origem: '' };

const CLASSE_SCORE: Record<Score, string> = {
  BRONZE: 'bg-bronze/15 text-bronze',
  PRATA: 'bg-prata/15 text-prata',
  OURO: 'bg-ouro/20 text-alerta',
  DIAMANTE: 'bg-diamante/15 text-diamante',
};

/**
 * Funil comercial.
 *
 * Quadro e lista mostram os mesmos dados, e por isso são abas e não duas telas:
 * a coluna dá a leitura de prioridade, a lista dá as colunas que não cabem num
 * card, e o painel lateral conduz a Oportunidade de etapa em etapa.
 *
 * Empilhadas, como estavam, as duas competiam pela mesma função sem a página
 * dizer qual era a principal — e a de baixo ia ficando atrás de uma rolagem
 * proporcional ao tamanho da base.
 */
export function PaginaSales(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [cadastroAberto, setCadastroAberto] = useState(false);
  const [cadenciaAberta, setCadenciaAberta] = useState(false);
  /**
   * Qual vista do funil está à mostra.
   *
   * O quadro e a lista são as mesmas Oportunidades, da mesma consulta, e antes
   * apareciam empilhadas: com treze dava para rolar, com trezentas a lista
   * ficava atrás de uma rolagem que ninguém alcança. Pior que o espaço, as duas
   * competiam pela mesma função e a página não dizia qual era a principal.
   *
   * Começa no quadro porque é onde a Comercial trabalha: a contagem por coluna
   * é a leitura de gargalo, e a lista não dá isso.
   */
  const [vista, setVista] = useState<'quadro' | 'lista'>('quadro');
  /**
   * O cartão aberto e o arrastado ficam guardados inteiros, e não pelo id: cada
   * coluna carrega aos poucos (NEX-227), e já não existe uma lista única onde
   * procurá-los. O que se guarda é o cartão como estava no clique.
   */
  const [selecionada, setSelecionada] = useState<Oportunidade | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VAZIOS);
  const [arrastando, setArrastando] = useState<Oportunidade | null>(null);
  const [colunaAlvo, setColunaAlvo] = useState<EtapaFunil | null>(null);
  /**
   * O movimento que o arrasto pediu e que ainda não pôde ser gravado, porque a
   * API exige próximo passo ou motivo. Abre a janelinha que cobra só isso.
   */
  const [movimento, setMovimento] = useState<{
    oportunidade: Oportunidade;
    destino: EtapaFunil;
  } | null>(null);
  const { usuaria } = useSessao();
  const ehAdministradora = usuaria?.papel === 'ADMINISTRADORA';
  const origens = useOrigens();
  /**
   * Seleção para atribuir várias de uma vez (NEX-226). Só a Administradora
   * distribui o atendimento, então só ela vê o modo. Enquanto ele está ligado,
   * clicar no cartão marca em vez de abrir, e o arrasto desliga: um gesto não
   * pode querer dizer duas coisas.
   */
  const [modoSelecao, setModoSelecao] = useState(false);
  /** Cada marcada com a sua etapa, para a coluna saber quantas dela estão marcadas. */
  const [marcadas, setMarcadas] = useState<ReadonlyMap<string, EtapaFunil>>(new Map());
  const [destinoDoLote, setDestinoDoLote] = useState('');
  const [avisoDoLote, setAvisoDoLote] = useState<string | null>(null);

  /**
   * Os números do Sales, calculados no servidor sobre todas as Oportunidades
   * nos filtros. Até a NEX-227 saíam da lista carregada, que parava em 500 — e
   * com três mil, mostrava Lead com 500 e as outras etapas zeradas.
   */
  const resumo = useQuery({
    queryKey: ['oportunidades', 'resumo', filtros],
    queryFn: () => chamarApi<ResumoDoPainel>(`/oportunidades/resumo${montarConsulta(filtros)}`),
  });

  /** A aba Lista também carrega aos poucos, e só quando está à mostra. */
  const lista = useInfiniteQuery({
    queryKey: ['oportunidades', 'lista', filtros],
    queryFn: ({ pageParam }) =>
      chamarApi<Oportunidade[]>(`/oportunidades${montarConsulta(filtros, { cursor: pageParam })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: proximaPagina,
    enabled: vista === 'lista',
  });
  const listaCarregada = lista.data?.pages.flat() ?? [];

  const usuarias = useQuery({
    queryKey: ['usuarias'],
    queryFn: () => chamarApi<UsuariaDaLista[]>('/usuarias'),
    enabled: ehAdministradora,
  });

  const transferirLote = useMutation({
    mutationFn: (corpo: { oportunidadeIds: string[]; responsavelId: string }) =>
      chamarApi<{ transferidas: number; jaEramDela: number }>(
        '/oportunidades/transferir-em-lote',
        { metodo: 'POST', corpo },
      ),
    onSuccess: async (resultado) => {
      const nome = usuarias.data?.find((uma) => uma.id === destinoDoLote)?.nome ?? 'a usuária';
      setAvisoDoLote(
        `${resultado.transferidas} ${resultado.transferidas === 1 ? 'atribuída' : 'atribuídas'} a ${nome}` +
          (resultado.jaEramDela > 0
            ? ` · ${resultado.jaEramDela} já ${resultado.jaEramDela === 1 ? 'era dela' : 'eram dela'}`
            : '') +
          '.',
      );
      sairDaSelecao();
      // A transferência move junto cadências e tarefas, que outras listas mostram.
      await Promise.all(
        ['oportunidades', 'cadencias', 'tarefas', 'dashboard', 'atender-agora'].map((chave) =>
          clienteDeConsultas.invalidateQueries({ queryKey: [chave] }),
        ),
      );
    },
  });

  function sairDaSelecao(): void {
    setModoSelecao(false);
    setMarcadas(new Map());
    setDestinoDoLote('');
  }

  function alternarMarcada(oportunidade: Oportunidade): void {
    setMarcadas((atuais) => {
      const novas = new Map(atuais);
      if (novas.has(oportunidade.id)) novas.delete(oportunidade.id);
      else novas.set(oportunidade.id, oportunidade.etapa);
      return novas;
    });
  }

  const marcadasPorEtapa = (etapa: EtapaFunil): number =>
    [...marcadas.values()].filter((daMarcada) => daMarcada === etapa).length;

  /**
   * Marca a etapa inteira — também os cartões que a coluna ainda não carregou,
   * pedindo os ids ao servidor. Com qualquer cartão dela marcado, desmarca.
   */
  const alternarEtapa = useMutation({
    mutationFn: async (etapa: EtapaFunil) => {
      if (marcadasPorEtapa(etapa) > 0) return { etapa, desmarcar: true as const };
      const resposta = await chamarApi<{ ids: string[]; passouDoTeto: boolean }>(
        `/oportunidades/ids${montarConsulta({ ...filtros, etapa })}`,
      );
      return { etapa, desmarcar: false as const, ...resposta };
    },
    onSuccess: (resultado) => {
      setMarcadas((atuais) => {
        const novas = new Map(atuais);
        for (const [id, etapa] of atuais) {
          if (etapa === resultado.etapa) novas.delete(id);
        }
        if (!resultado.desmarcar) {
          for (const id of resultado.ids) novas.set(id, resultado.etapa);
        }
        return novas;
      });
      setAvisoDoLote(
        !resultado.desmarcar && resultado.passouDoTeto
          ? `${ROTULO_ETAPA[resultado.etapa]} tem mais de 500: foram marcadas as 500 mais novas. Atribua e marque de novo para seguir.`
          : null,
      );
    },
  });

  /**
   * A lista como planilha, com os filtros que estão na tela.
   *
   * Quem filtrou por etapa e responsável espera levar aquilo: baixar o funil
   * inteiro seria outra resposta para o mesmo botão.
   */
  const baixarPlanilha = useMutation({
    mutationFn: () => baixarArquivo(`/oportunidades/planilha${montarConsulta(filtros)}`),
  });

  const modeloDeCadencia = useModeloDeCadencia();

  const moverEtapa = useMutation({
    mutationFn: ({ id, etapa }: { id: string; etapa: EtapaFunil }) =>
      chamarApi<Oportunidade>(`/oportunidades/${id}/etapa`, { metodo: 'PATCH', corpo: { etapa } }),
    onSuccess: async () => {
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['oportunidades'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['contatos'] });
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível mover a Oportunidade.');
    },
  });

  /**
   * Solta o cartão numa coluna.
   *
   * Arrastar move, e só move: nenhum campo da venda viaja junto. Quem ficar
   * sem próximo passo é cobrado pelo indicador do painel e pelo aviso no
   * cartão, que existem para isso — o ADR 0011 conta por que deixou de travar.
   *
   * Sobram dois casos, e por motivos diferentes. Reciclagem pede o motivo,
   * porque ele não tem aviso nenhum e ninguém o reconstrói um mês depois.
   * Fechado pede confirmação, porque cria o Contrato e abre a Implantação, e
   * isso não deve acontecer por um gesto torto.
   */
  function aoSoltarNaEtapa(etapa: EtapaFunil): void {
    const oportunidade = arrastando;
    setArrastando(null);
    setColunaAlvo(null);
    if (!oportunidade || oportunidade.etapa === etapa) return;

    setErro(null);
    if (exigeMotivo(etapa) || etapa === 'FECHADO') {
      setMovimento({ oportunidade, destino: etapa });
      return;
    }

    moverEtapa.mutate({ id: oportunidade.id, etapa });
  }

  const emDetalhe = selecionada;
  const aMover = movimento?.oportunidade;

  return (
    <section>
      <CabecalhoDePagina
        area="Comercial"
        titulo="Reducee Sales"
        descricao="Funil, produção, pagamentos e origem das vendas."
        acoes={
          <button
            type="button"
            onClick={() => setCadastroAberto(true)}
            className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro"
          >
            + Nova oportunidade
          </button>
        }
      />

      <FaixaDoFunil porEtapa={resumo.data?.porEtapa} />

      {erro && !emDetalhe ? (
        <p role="alert" className="mt-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      <Cartao
        titulo="Gestão comercial"
        descricao="Prioridade, SLA, origem e próximos passos."
        className="mt-3"
      >
      <div className="flex flex-wrap items-end gap-3">
        <CampoTexto
          rotulo="Buscar"
          name="busca"
          placeholder="Contato, produto ou operadora"
          value={filtros.busca}
          onChange={(evento) => setFiltros((atual) => ({ ...atual, busca: evento.target.value }))}
          className="min-w-56 flex-1"
        />
        <CampoSelecao
          rotulo="Etapa"
          name="filtroEtapa"
          value={filtros.etapa}
          onChange={(evento) => setFiltros((atual) => ({ ...atual, etapa: evento.target.value }))}
        >
          <option value="">Todas</option>
          {ETAPAS_FUNIL.map((etapa) => (
            <option key={etapa} value={etapa}>
              {ROTULO_ETAPA[etapa]}
            </option>
          ))}
        </CampoSelecao>
        <CampoSelecao
          rotulo="Origem"
          name="filtroOrigem"
          value={filtros.origem}
          onChange={(evento) => setFiltros((atual) => ({ ...atual, origem: evento.target.value }))}
        >
          <option value="">Todas</option>
          {/* Desativadas também: filtrar o histórico por elas continua valendo. */}
          {origens.todas.map((origem) => (
            <option key={origem.codigo} value={origem.codigo}>
              {origem.nome}
            </option>
          ))}
        </CampoSelecao>
        {ehAdministradora ? (
          <CampoSelecao
            rotulo="Responsável"
            name="filtroResponsavel"
            value={filtros.responsavelId}
            onChange={(evento) =>
              setFiltros((atual) => ({ ...atual, responsavelId: evento.target.value }))
            }
          >
            <option value="">Todas</option>
            {(usuarias.data ?? [])
              .filter((candidata) => candidata.ativa)
              .map((candidata) => (
                <option key={candidata.id} value={candidata.id}>
                  {candidata.nome}
                </option>
              ))}
          </CampoSelecao>
        ) : null}

        <button
          type="button"
          onClick={() => setFiltros(FILTROS_VAZIOS)}
          className="rounded-lg border border-linha px-3 py-2 text-sm transition hover:bg-marinho-suave"
        >
          Limpar filtros
        </button>

      </div>

        <div className="mt-4">
          <IndicadoresDeGestao resumo={resumo.data} />
        </div>
      </Cartao>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1.5fr_1fr]">
        <Cartao
          titulo="Atender agora"
          descricao="Fila automática por score, atraso de primeiro contato e tempo do lead."
        >
          <FilaDeAtendimento limite={5} />
        </Cartao>
        <Cartao
          titulo="Conversão por origem / campanha"
          descricao="Leads, propostas, fechamentos e valor vendido."
        >
          <ConversaoPorOrigem linhas={resumo.data?.conversao} />
        </Cartao>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1.5fr_1fr]">
        <Cartao
          titulo="Cadência Comercial Automática"
          descricao="Sequência de contatos para leads ativos, com Ligação, WhatsApp, E-mail e Social Point."
        >
          {modeloDeCadencia ? (
            <ResumoDoRoteiro modelo={modeloDeCadencia} />
          ) : (
            <p className="text-texto-suave">
              O roteiro de cadência é configurado pela Administradora.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCadenciaAberta(true)}
              className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro"
            >
              Iniciar cadência
            </button>
            {ehAdministradora ? (
              <Link
                to="/cadencias"
                className="rounded-[10px] border border-linha px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
              >
                Configurar modelo
              </Link>
            ) : null}
          </div>
        </Cartao>
        <Cartao titulo="Contatos de hoje" descricao="Próximas ações geradas pelas cadências.">
          <ContatosDeHoje />
        </Cartao>
      </div>

      <div className="mt-5">
        <h2 className="font-semibold text-marinho">Cadências em andamento</h2>
        <p className="mb-3 text-sm text-texto-suave">Histórico e próxima ação por lead.</p>
        <TabelaDeCadencias />
      </div>

      <div className="mt-5 rounded-2xl border border-linha bg-superficie p-4.25 shadow-card">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-base font-extrabold">Pipeline comercial</div>
            <div className="text-xs text-texto-suave">
              {vista === 'quadro'
                ? 'Lead → Qualificado → Reunião → Proposta → Negociação → Fechado. Perdidos podem ir para reciclagem.'
                : 'Base comercial consolidada.'}
            </div>
          </div>
          {/*
            O botão sobe para o cabeçalho do bloco: antes morava só na tabela, e
            com ela atrás de uma aba quem estivesse no quadro perderia o acesso
            a registrar venda sem entender por quê.
          */}
          <div className="flex flex-wrap items-center gap-2">
            {/*
              Baixar vale para os dois papéis: a rota recorta por responsável,
              então a Comercial leva a lista dela e não a de mais ninguém.
            */}
            <button
              type="button"
              data-teste="baixar-pipeline"
              disabled={baixarPlanilha.isPending}
              onClick={() => baixarPlanilha.mutate()}
              className="rounded-[10px] border border-linha px-3.25 py-2.5 text-base font-[750] text-marinho transition hover:bg-marinho-suave disabled:opacity-60"
            >
              {baixarPlanilha.isPending ? 'Baixando…' : 'Baixar planilha'}
            </button>

            {ehAdministradora ? (
              <button
                type="button"
                data-teste="selecionar-pipeline"
                aria-pressed={modoSelecao}
                onClick={() => {
                  setAvisoDoLote(null);
                  if (modoSelecao) {
                    sairDaSelecao();
                    return;
                  }
                  // A seleção vive no quadro, onde as etapas estão à vista.
                  setVista('quadro');
                  setModoSelecao(true);
                }}
                className="rounded-[10px] border border-linha px-3.25 py-2.5 text-base font-[750] text-marinho transition hover:bg-marinho-suave"
              >
                {modoSelecao ? 'Cancelar seleção' : 'Selecionar'}
              </button>
            ) : null}

            {ehAdministradora ? (
              <button
                type="button"
                onClick={() => setCadastroAberto(true)}
                className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro"
              >
                + Registrar
              </button>
            ) : null}
          </div>
        </div>

        {modoSelecao ? (
          <div
            role="region"
            aria-label="Atribuir selecionadas"
            data-teste="barra-de-selecao"
            className="mb-3 flex flex-wrap items-end gap-3 rounded-[11px] border border-marinho-claro bg-marinho-suave p-3"
          >
            <p className="text-sm font-[750] text-marinho" aria-live="polite">
              {marcadas.size === 0
                ? 'Marque os cartões, ou a etapa inteira.'
                : `${marcadas.size} ${marcadas.size === 1 ? 'selecionada' : 'selecionadas'}`}
            </p>
            <CampoSelecao
              rotulo="Atribuir a"
              name="destinoDoLote"
              value={destinoDoLote}
              onChange={(evento) => setDestinoDoLote(evento.target.value)}
              className="min-w-[180px]"
            >
              <option value="" disabled>
                Selecione
              </option>
              {(usuarias.data ?? [])
                .filter((uma) => uma.ativa)
                .map((uma) => (
                  <option key={uma.id} value={uma.id}>
                    {uma.nome}
                  </option>
                ))}
            </CampoSelecao>
            <button
              type="button"
              disabled={
                marcadas.size === 0 ||
                marcadas.size > TETO_DO_LOTE ||
                destinoDoLote === '' ||
                transferirLote.isPending
              }
              onClick={() =>
                transferirLote.mutate({
                  oportunidadeIds: [...marcadas.keys()],
                  responsavelId: destinoDoLote,
                })
              }
              className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
            >
              {transferirLote.isPending ? 'Atribuindo…' : 'Atribuir'}
            </button>
            <p className="basis-full text-xs text-texto-suave">
              Leva junto o Lead, as cadências em andamento e as tarefas abertas de cada uma.
            </p>
            {marcadas.size > TETO_DO_LOTE ? (
              <p role="alert" data-teste="lote-acima-do-teto" className="basis-full text-sm text-erro">
                Atribua no máximo {TETO_DO_LOTE} por vez. Desmarque{' '}
                {marcadas.size - TETO_DO_LOTE} para seguir.
              </p>
            ) : null}
            {transferirLote.isError ? (
              <p role="alert" className="basis-full text-sm text-erro">
                {transferirLote.error instanceof ErroDaApi
                  ? transferirLote.error.message
                  : 'Não foi possível atribuir as selecionadas.'}
              </p>
            ) : null}
          </div>
        ) : null}

        {avisoDoLote ? (
          <p
            role="status"
            data-teste="aviso-do-lote"
            className="mb-3 rounded-lg bg-sucesso/10 px-3 py-2 text-sm text-sucesso"
          >
            {avisoDoLote}
          </p>
        ) : null}

        {baixarPlanilha.isError ? (
          <p role="alert" className="mb-3 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
            {baixarPlanilha.error instanceof ErroDaApi
              ? baixarPlanilha.error.message
              : 'Não foi possível baixar a planilha.'}
          </p>
        ) : null}

        {/*
          As duas vistas da mesma consulta, uma de cada vez. Trocar de aba não
          busca nada: `listaDeOportunidades` já está em memória, e é isso que
          torna a aba honesta — se fossem dados diferentes, seriam duas telas.
        */}
        <div role="tablist" aria-label="Vista do funil" className="mb-3 flex gap-1">
          {(
            [
              ['quadro', 'Funil'],
              ['lista', 'Lista'],
            ] as const
          ).map(([qual, rotulo]) => (
            <button
              key={qual}
              type="button"
              role="tab"
              aria-selected={vista === qual}
              data-teste={`aba-${qual}`}
              onClick={() => setVista(qual)}
              className={`rounded-[10px] px-3.25 py-2 text-xs font-[750] transition ${
                vista === qual
                  ? 'bg-marinho text-white'
                  : 'bg-coluna text-texto-suave hover:text-marinho'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>

        {vista === 'lista' ? (
          <>
            <TabelaDeVendas
              oportunidades={listaCarregada}
              aoSelecionar={(id) =>
                setSelecionada(listaCarregada.find((item) => item.id === id) ?? null)
              }
            />
            {lista.hasNextPage ? (
              <button
                type="button"
                data-teste="lista-carregar-mais"
                disabled={lista.isFetchingNextPage}
                onClick={() => void lista.fetchNextPage()}
                className="mt-3 w-full rounded-[10px] border border-linha px-3 py-2 text-sm font-[750] text-marinho transition hover:bg-marinho-suave disabled:opacity-60"
              >
                {lista.isFetchingNextPage
                  ? 'Carregando…'
                  : `Carregar mais · ${listaCarregada.length} de ${somaDoResumo(resumo.data)}`}
              </button>
            ) : null}
          </>
        ) : (
          <div
            data-teste="quadro-oportunidades"
            className="grid grid-flow-col auto-cols-[minmax(180px,1fr)] gap-2.25 overflow-auto pb-1"
          >
            {ETAPAS_VISIVEIS.map((etapa) => (
              <ColunaDoQuadro
                key={etapa}
                etapa={etapa}
                filtros={filtros}
                total={resumo.data?.porEtapa[etapa]}
                valorTotalCentavos={resumo.data?.valorPorEtapaCentavos[etapa]}
                modoSelecao={modoSelecao}
                marcadas={marcadas}
                marcadasNaEtapa={marcadasPorEtapa(etapa)}
                aoAlternarMarcada={alternarMarcada}
                aoAlternarEtapa={() => alternarEtapa.mutate(etapa)}
                alternandoEtapa={alternarEtapa.isPending && alternarEtapa.variables === etapa}
                selecionadaId={selecionada?.id ?? null}
                aoAbrir={(oportunidade) => {
                  setSelecionada(oportunidade);
                  setErro(null);
                }}
                arrastando={arrastando}
                aoArrastar={setArrastando}
                alvo={colunaAlvo === etapa && arrastando !== null}
                aoEntrarNoAlvo={() => setColunaAlvo(etapa)}
                aoSairDoAlvo={() => setColunaAlvo((atual) => (atual === etapa ? null : atual))}
                aoFimDoArrasto={() => {
                  setArrastando(null);
                  setColunaAlvo(null);
                }}
                aoSoltar={() => aoSoltarNaEtapa(etapa)}
              />
            ))}
          </div>
        )}
      </div>

      {cadastroAberto ? <NovaOportunidade aoFechar={() => setCadastroAberto(false)} /> : null}

      {cadenciaAberta ? (
        <IniciarCadencia aoFechar={() => setCadenciaAberta(false)} />
      ) : null}

      {/* Clicar no cartão abre a mesma janela do protótipo, preenchida. O
        * arrasto não passa mais por aqui: ele tem a janelinha abaixo. */}
      {emDetalhe ? (
        <NovaOportunidade
          key={emDetalhe.id}
          oportunidade={emDetalhe}
          aoFechar={() => setSelecionada(null)}
        />
      ) : null}

      {aMover && movimento ? (
        <MoverEtapa
          key={`${aMover.id}-${movimento.destino}`}
          oportunidade={aMover}
          destino={movimento.destino}
          aoFechar={() => setMovimento(null)}
        />
      ) : null}

    </section>
  );
}

/**
 * Uma etapa do quadro, com os seus cartões carregados aos poucos (NEX-227).
 *
 * Cada coluna pede 50 por vez e busca mais quando a rolagem dela chega ao fim.
 * É componente próprio porque cada uma tem a sua consulta, e consulta não se
 * cria dentro de laço. Os números no topo, a quantidade e o valor, são os do
 * servidor, sobre todas — não os dos cartões que já chegaram.
 */
function ColunaDoQuadro({
  etapa,
  filtros,
  total,
  valorTotalCentavos,
  modoSelecao,
  marcadas,
  marcadasNaEtapa,
  aoAlternarMarcada,
  aoAlternarEtapa,
  alternandoEtapa,
  selecionadaId,
  aoAbrir,
  arrastando,
  aoArrastar,
  alvo,
  aoEntrarNoAlvo,
  aoSairDoAlvo,
  aoFimDoArrasto,
  aoSoltar,
}: {
  etapa: EtapaFunil;
  filtros: Filtros;
  total: number | undefined;
  valorTotalCentavos: number | undefined;
  modoSelecao: boolean;
  marcadas: ReadonlyMap<string, EtapaFunil>;
  marcadasNaEtapa: number;
  aoAlternarMarcada: (oportunidade: Oportunidade) => void;
  aoAlternarEtapa: () => void;
  alternandoEtapa: boolean;
  selecionadaId: string | null;
  aoAbrir: (oportunidade: Oportunidade) => void;
  arrastando: Oportunidade | null;
  aoArrastar: (oportunidade: Oportunidade) => void;
  alvo: boolean;
  aoEntrarNoAlvo: () => void;
  aoSairDoAlvo: () => void;
  aoFimDoArrasto: () => void;
  aoSoltar: () => void;
}): React.JSX.Element {
  // Com o filtro de etapa ligado, as outras colunas nem perguntam: seriam vazias.
  const visivel = filtros.etapa === '' || filtros.etapa === etapa;
  const cartoes = useInfiniteQuery({
    queryKey: ['oportunidades', 'coluna', etapa, filtros],
    queryFn: ({ pageParam }) =>
      chamarApi<Oportunidade[]>(
        `/oportunidades${montarConsulta({ ...filtros, etapa }, { cursor: pageParam })}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: proximaPagina,
    enabled: visivel,
  });
  const daEtapa = visivel ? (cartoes.data?.pages.flat() ?? []) : [];
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = cartoes;

  // Venda encerrada não entra no lote: a API recusa, e a tela nem oferece
  // (NEX-226).
  const selecionavel = modoSelecao && estaAberta(etapa);
  const quantidade = visivel ? (total ?? daEtapa.length) : 0;
  // Sem o resumo não há como somar: somar os cartões que já chegaram daria um
  // total parcial, então a linha fica vazia até o servidor responder.
  const valorTotal = visivel ? valorTotalCentavos : 0;

  /**
   * Ao chegar ao fim da rolagem da coluna, busca a próxima página. O marco
   * fica logo abaixo do último cartão e é observado dentro da própria coluna,
   * não da página — é a rolagem dela que importa.
   */
  const rolagem = useRef<HTMLDivElement>(null);
  const fim = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const marco = fim.current;
    if (!marco || !hasNextPage) return;
    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: rolagem.current, rootMargin: '0px 0px 200px 0px' },
    );
    observador.observe(marco);
    return () => observador.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div
      data-teste={`coluna-${etapa}`}
      onDragOver={(evento) => {
        // Sem preventDefault o navegador recusa a soltura.
        evento.preventDefault();
        aoEntrarNoAlvo();
      }}
      onDragLeave={aoSairDoAlvo}
      onDrop={(evento) => {
        evento.preventDefault();
        aoSoltar();
      }}
      className={`min-h-[220px] rounded-[13px] bg-coluna p-2.25 transition ${
        alvo ? 'ring-2 ring-marinho-claro' : 'ring-2 ring-transparent'
      }`}
    >
      <div className="mb-2 text-[#5c6d79]">
        <div className="flex justify-between text-[10px] font-black uppercase">
          <span>{ROTULO_ETAPA[etapa]}</span>
          <span data-teste={`total-da-coluna-${etapa}`}>{quantidade}</span>
        </div>
        {/* A linha existe antes do número chegar, para a coluna não pular. */}
        <div
          data-teste={`valor-da-coluna-${etapa}`}
          className="mt-0.5 min-h-4 whitespace-nowrap text-[11px] font-extrabold leading-4 text-marinho"
        >
          {valorTotal === undefined ? null : formatarReais(valorTotal)}
        </div>
      </div>

      {selecionavel && quantidade > 0 ? (
        <button
          type="button"
          data-teste={`selecionar-etapa-${etapa}`}
          disabled={alternandoEtapa}
          onClick={aoAlternarEtapa}
          className="mb-2 w-full rounded-[9px] border border-dashed border-marinho-claro px-2 py-1.5 text-[11px] font-[750] text-marinho transition hover:bg-superficie disabled:opacity-60"
        >
          {alternandoEtapa
            ? 'Marcando…'
            : marcadasNaEtapa > 0
              ? `Desmarcar (${marcadasNaEtapa})`
              : `Selecionar todos (${quantidade})`}
        </button>
      ) : null}

      {/*
        Cada etapa rola por dentro. Empilhados na página, os cartões faziam a
        coluna mais longa ditar a altura de tudo: com mil leads, chegar ao fim
        de Lead era rolar a tela inteira, e as outras etapas ficavam lá em
        cima, fora de vista.
      */}
      <div
        ref={rolagem}
        data-teste={`lista-da-coluna-${etapa}`}
        className="max-h-[min(70vh,640px)] overflow-y-auto overscroll-contain"
      >
        {daEtapa.map((oportunidade) => {
          const marcada = marcadas.has(oportunidade.id);

          return (
            <button
              key={oportunidade.id}
              type="button"
              data-teste="oportunidade"
              draggable={!modoSelecao}
              disabled={modoSelecao && !selecionavel}
              aria-pressed={selecionavel ? marcada : undefined}
              onDragStart={(evento) => {
                aoArrastar(oportunidade);
                evento.dataTransfer.effectAllowed = 'move';
                // O Firefox só inicia o arrasto se houver carga.
                evento.dataTransfer.setData('text/plain', oportunidade.id);
              }}
              onDragEnd={aoFimDoArrasto}
              onClick={() => {
                if (modoSelecao) {
                  aoAlternarMarcada(oportunidade);
                  return;
                }
                aoAbrir(oportunidade);
              }}
              className={`mb-2 block w-full rounded-[11px] border bg-superficie p-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
                modoSelecao ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
              } ${arrastando?.id === oportunidade.id ? 'opacity-40' : ''} ${
                modoSelecao && marcada
                  ? 'border-marinho ring-2 ring-marinho-claro'
                  : selecionadaId === oportunidade.id && !modoSelecao
                    ? 'border-marinho'
                    : 'border-linha hover:border-marinho-claro'
              }`}
            >
              {selecionavel ? (
                <span
                  aria-hidden
                  className={`float-right ml-1 flex size-4 items-center justify-center rounded border text-[10px] leading-none ${
                    marcada ? 'border-marinho bg-marinho text-white' : 'border-linha bg-superficie'
                  }`}
                >
                  {marcada ? '✓' : ''}
                </span>
              ) : null}
              <b className="block text-[11px]">{oportunidade.contato.nome}</b>
              <span className="text-[10px] text-texto-suave">
                {ROTULO_SCORE[oportunidade.score]} · {oportunidade.operadora ?? '—'} ·{' '}
                {oportunidade.responsavel.nome}
              </span>
              <div className="mt-1.75 font-extrabold">
                {formatarReais(oportunidade.valorMensalCentavos)}
              </div>
              <ResumoDoFormulario resumo={oportunidade.resumoDoFormulario} />
              <span className="text-[10px] text-texto-suave">
                {oportunidade.proximoPasso
                  ? `Próx.: ${oportunidade.proximoPasso}${oportunidade.dataProximoPasso ? ` · ${data(oportunidade.dataProximoPasso)}` : ''}`
                  : exigeProximoPasso(oportunidade.etapa)
                    ? '⚠ Sem próximo passo'
                    : ''}
              </span>
            </button>
          );
        })}

        <div ref={fim} aria-hidden className="h-px" />
        {hasNextPage ? (
          <button
            type="button"
            data-teste={`carregar-mais-${etapa}`}
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
            className="w-full rounded-[9px] px-2 py-1.5 text-[11px] font-[750] text-marinho transition hover:bg-superficie disabled:opacity-60"
          >
            {isFetchingNextPage
              ? 'Carregando…'
              : `Carregar mais · ${daEtapa.length} de ${quantidade}`}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Quantas Oportunidades o resumo conta ao todo, nos filtros da tela. */
function somaDoResumo(resumo: ResumoDoPainel | undefined): number | string {
  if (!resumo) return '…';
  return Object.values(resumo.porEtapa).reduce((total, quantidade) => total + quantidade, 0);
}

/**
 * O que a pessoa paga hoje e as idades de quem entra, abaixo do valor do card.
 *
 * Fica colada no valor estimado de propósito: é a comparação que a Comercial
 * faz antes de ligar, e ler as duas de uma vez poupa abrir a ficha.
 *
 * Pergunta sem resposta some da linha, e linha sem nenhuma resposta some
 * inteira. Traço ou zero no lugar do vazio se leriam como algo que a pessoa
 * respondeu — e ninguém respondeu.
 */
function ResumoDoFormulario({
  resumo,
}: {
  resumo: Oportunidade['resumoDoFormulario'];
}): React.JSX.Element | null {
  if (!resumo) return null;

  // A mensalidade vai crua, como foi digitada — `R$ 800`, `800,00` ou `em
  // torno de 1.200`. Normalizar seria adivinhar, e quem lê entende as três.
  const partes = [
    resumo.mensalidadeAtual ? `Paga hoje ${resumo.mensalidadeAtual}` : null,
    resumo.idades ? `Idades ${resumo.idades}` : null,
  ].filter((parte) => parte !== null);

  if (partes.length === 0) return null;

  return (
    <span data-teste="oportunidade-resumo-formulario" className="block text-[10px] text-texto-suave">
      {partes.join(' · ')}
    </span>
  );
}

/** Quantos cartões cada coluna e a aba Lista trazem por vez (NEX-227). */
const POR_PAGINA = 50;

/** O teto da transferência em lote, o mesmo que a API impõe. */
const TETO_DO_LOTE = 500;

function montarConsulta(
  filtros: Filtros,
  pagina?: { cursor: string | undefined },
): string {
  const parametros = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor !== '') parametros.set(chave, valor);
  }
  if (pagina) {
    parametros.set('limite', String(POR_PAGINA));
    if (pagina.cursor) parametros.set('cursor', pagina.cursor);
  }
  const consulta = parametros.toString();
  return consulta === '' ? '' : `?${consulta}`;
}

/** Página cheia sugere que há mais; o cursor é o último cartão recebido. */
function proximaPagina(ultima: Oportunidade[]): string | undefined {
  return ultima.length === POR_PAGINA ? ultima.at(-1)?.id : undefined;
}
